import { parse } from 'yaml'

function isValidYAML(input: string): boolean {
  try {
    const parsed = parse(input)
    return typeof parsed !== 'undefined'
  } catch {
    return false
  }
}

export function parseYAMLValues(
  obj: Record<string, unknown>
): Record<string, unknown> {
  if (typeof obj !== 'object' || obj === null) return obj

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (typeof value === 'string' && isValidYAML(value)) {
      acc[key] = parse(value)
    } else if (typeof value === 'object' && value !== null) {
      acc[key] = parseYAMLValues(value as Record<string, unknown>)
    } else {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, unknown>)
}

if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it.each([
    ['a: 1\nb: 2', true],
    ['- item1\n- item2', true],
    ['key: value', true],
    ['123', true],
    ['true', true],
    ['{ key: value }', true],
    ['[1, 2, 3]', true],
    ['invalid_yaml: : ', false],
    ['no: value: here', false],
    ['- missing: value\n- key', true],
  ])('isValidYaml(%s)', (testValue, expected) => {
    expect(isValidYAML(testValue)).toBe(expected)
  })
  it.each([
    { expected: { simple: { a: 1, b: 2 } }, object: { simple: 'a: 1\nb: 2' } },
    {
      expected: { list: ['item1', 'item2'] },
      object: { list: '- item1\n- item2' },
    },
    {
      expected: { mixed: { key1: 'value1', key2: { subkey: 'subvalue' } } },
      object: { mixed: 'key1: value1\nkey2:\n  subkey: subvalue' },
    },
    {
      expected: { numbers: { one: 1, three: 3, two: 2 } },
      object: { numbers: 'one: 1\ntwo: 2\nthree: 3' },
    },
    { expected: { booleanTest: true }, object: { booleanTest: 'true' } },
    {
      expected: {
        objectTest: { age: 30, name: 'John' },
      },
      object: { objectTest: '{ name: John, age: 30 }' },
    },
    {
      expected: { rawNull: null, rawNumber: 42 },
      object: { rawNull: null, rawNumber: 42 },
    },
    {
      expected: {
        arrayTest: [1, 2, { nested: 'value' }],
      },
      object: { arrayTest: '[1, 2, { nested: value }]' },
    },
    {
      expected: {
        deepNested: { yamlString: { level1: { level2: { level3: 'value' } } } },
      },
      object: {
        deepNested: { yamlString: 'level1:\n  level2:\n    level3: value' },
      },
    },
    {
      expected: {
        multipleYAML: {
          first: { a: 1 },
          second: { b: 2 },
          third: { c: { d: 3 } },
        },
      },
      object: {
        multipleYAML: { first: 'a: 1', second: 'b: 2', third: 'c:\n  d: 3' },
      },
    },
    {
      expected: {
        edgeCase: [{ not_yaml: 'here' }, { key: 'value' }],
      },
      object: { edgeCase: '- not_yaml: here\n- key: value' },
    },
  ])('parseYAMLValues($object)', ({ expected, object }) => {
    expect(parseYAMLValues(object)).toEqual(expected)
  })
  it('returns non-objects unchanged', () => {
    expect(
      parseYAMLValues('not-yaml' as unknown as Record<string, unknown>)
    ).toBe('not-yaml')
    expect(
      parseYAMLValues(null as unknown as Record<string, unknown>)
    ).toBeNull()
  })
}
